// OrgLogo — the single way to show an organisation's identity (#40). Renders the
// uploaded logo when there is one, otherwise a soft, name-derived colored
// placeholder with the org's initials. Reused everywhere an org is represented
// (job cards, job detail, org headers).
//
// Optional `badge` overlays a small entity-type glyph in the corner (e.g. a
// briefcase for a job posting) so you can tell WHAT the entity is while still
// seeing WHOSE it is. The same pattern is intended for courses later — org logo +
// a course-type badge — so this stays generic.
import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { orgPlaceholderColor, orgInitials } from '@/lib/orgLogo';
import { cn } from '@/lib/utils';

type OrgLogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<OrgLogoSize, { box: string; text: string; radius: string; badge: string; badgeGlyph: string }> = {
    // xs (#354) — sits INSIDE a line of text, where the org rides along with a
    // label rather than leading a row (a table cell, the org line under a user's
    // unit). Everything larger leads a row or a header.
    // rounded-xl on a 20px box reads as a circle, which is a different shape
    // language from the squircle every larger size uses — so the radius scales too.
    xs: { box: 'w-5 h-5', text: 'text-[9px]', radius: 'rounded-md', badge: 'w-3 h-3 -bottom-0.5 -right-0.5', badgeGlyph: 'w-2 h-2' },
    sm: { box: 'w-9 h-9', text: 'text-xs', radius: 'rounded-xl', badge: 'w-4 h-4 -bottom-0.5 -right-0.5', badgeGlyph: 'w-2.5 h-2.5' },
    md: { box: 'w-11 h-11', text: 'text-sm', radius: 'rounded-xl', badge: 'w-5 h-5 -bottom-1 -right-1', badgeGlyph: 'w-3 h-3' },
    lg: { box: 'w-12 h-12', text: 'text-base', radius: 'rounded-xl', badge: 'w-5 h-5 -bottom-1 -right-1', badgeGlyph: 'w-3 h-3' },
    xl: { box: 'w-14 h-14', text: 'text-lg', radius: 'rounded-xl', badge: 'w-6 h-6 -bottom-1 -right-1', badgeGlyph: 'w-3.5 h-3.5' },
};

interface OrgLogoProps {
    name?: string;
    /** Raw logo URL from the API (relative or absolute); undefined/null → placeholder. */
    logoUrl?: string | null;
    size?: OrgLogoSize;
    /** Optional entity-type glyph shown as a corner badge (e.g. Briefcase for a need). */
    badge?: LucideIcon;
    className?: string;
}

export const OrgLogo: React.FC<OrgLogoProps> = ({ name, logoUrl, size = 'lg', badge: Badge, className }) => {
    const s = SIZES[size];
    const src = resolveMediaUrl(logoUrl);
    // Fall back to the colored placeholder if the image fails to load (deleted
    // object, network error). Reset when the URL changes so a fresh upload retries.
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);

    let inner: React.ReactNode;
    if (src && !failed) {
        inner = (
            <img
                src={src}
                alt={name ? `${name} logotyp` : 'Organisationslogotyp'}
                onError={() => setFailed(true)}
                className={cn('w-full h-full object-contain bg-white border border-gray-100', s.radius)}
            />
        );
    } else {
        const { bg, fg } = orgPlaceholderColor(name || '?');
        inner = (
            <span
                style={{ backgroundColor: bg, color: fg }}
                className={cn('w-full h-full flex items-center justify-center font-semibold select-none', s.radius, s.text)}
            >
                {orgInitials(name)}
            </span>
        );
    }

    return (
        <span
            className={cn('relative inline-block flex-shrink-0', s.box, className)}
            aria-label={name ? `${name} logotyp` : 'Organisationslogotyp'}
        >
            {inner}
            {Badge && (
                <span
                    className={cn(
                        'absolute flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-[#1A4D4F]',
                        s.badge,
                    )}
                >
                    <Badge className={s.badgeGlyph} />
                </span>
            )}
        </span>
    );
};

export default OrgLogo;
